import { useEffect, useRef, useState } from "react";
import { Search } from "../Search/Search";
import MentorGrid from "./helpers/MentorGrid";
import { useAppDispatch, useAppSelector } from "../../app/store";
import { mentorSearchEventBuilder, sendEvent } from "../../utils/event-helpers";
import type { Mentor, MentorPanel } from "../../shared/mentorTypes";
import styles from "./MentorBrowser.module.css";
import { CheckIcon } from "lucide-react";

export default function MentorBrowser({
  mentors,
  panels,
}: {
  mentors: Mentor[];
  panels: MentorPanel[];
}) {
  const [query, setQuery] = useState<string>("");
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  const { userId, sessionId } = useAppSelector((state) => state.session);
  const bookmarkedMentors = useAppSelector(
    (state) => state.mentorBookmarks.bookmarks,
  );

  const dispatch = useAppDispatch();

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const debounceFilteredMentorsRef = useRef<Mentor[]>([]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!userId || !sessionId) return;

    const newQuery = event.target.value;
    setQuery(newQuery);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      const eventData = mentorSearchEventBuilder(
        userId,
        sessionId,
        newQuery,
        debounceFilteredMentorsRef.current,
        {},
      );

      void dispatch(sendEvent(eventData));
    }, 500);
  };

  const filteredMentors = mentors.filter((mentor) => {
    const lowerQuery = query.toLowerCase();

    const matchesName = mentor.name.toLowerCase().includes(lowerQuery);
    const matchesTitle = mentor.title.toLowerCase().includes(lowerQuery);

    const isBookmarked = bookmarkedMentors
      .map((b) => b.mentorId)
      .includes(mentor._id);

    return (
      (!showBookmarkedOnly || isBookmarked) && (matchesName || matchesTitle)
    );
  });

  return (
    <>
      <div className={styles.mentorSearchContainer}>
        <Search
          query={query}
          handleSearchChange={handleSearchChange}
          dataCy="mentor-search-input"
        />
        <button
          type="button"
          className={[
            styles.recommendedButton,
            showBookmarkedOnly ? styles.recommendedButtonActive : "",
          ].join(" ")}
          aria-pressed={showBookmarkedOnly}
          onClick={() => {
            setShowBookmarkedOnly((current) => !current);
          }}
          data-cy="filter-bookmarked-button"
        >
          <span className={styles.checkIcon}>
            <CheckIcon />
          </span>
          Show Bookmarked
        </button>
      </div>

      <MentorGrid
        panels={panels}
        mentors={filteredMentors}
        bookmarkedMentors={bookmarkedMentors}
      />
    </>
  );
}
